/**
 * Silences "Failed to load resource" / NXDOMAIN console noise from third-party
 * tracker hosts that ad-blockers, Brave Shields, Safari ITP, Pi-hole, and
 * corporate DNS filters routinely block. These failures are expected for
 * 30-40% of US visitors and are not actionable — suppressing them keeps real
 * runtime errors visible.
 *
 * We do NOT swallow application errors. We only filter console.error messages
 * whose payload string mentions one of the tracker hosts below.
 */

const TRACKER_HOSTS = [
  // Google Ads / DoubleClick
  "stats.g.doubleclick.net",
  "googleads.g.doubleclick.net",
  "td.doubleclick.net",
  "googleadservices.com",
  "googlesyndication.com",
  // HubSpot tracking (forms host js.hsforms.net stays — it's required for forms)
  "hsadspixel.net",
  "hs-analytics.net",
  "hscollectedforms.net",
  "hs-banner.com",
  "hsleadflows.net",
  "hs-scripts.com",
  // LinkedIn Insight
  "px.ads.linkedin.com",
  "snap.licdn.com",
  "ddwl4m2hdecbv.cloudfront.net",
  // Unify
  "tag.unifyintent.com",
  "unifyintent.com",
];

const NOISE_PATTERNS = [
  /Failed to load resource/i,
  /server with the specified hostname could not be found/i,
  /net::ERR_BLOCKED_BY_CLIENT/i,
  /net::ERR_NAME_NOT_RESOLVED/i,
  /Refused to load/i,
];

const isTrackerNoise = (args: unknown[]): boolean => {
  const message = args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(" ");

  const mentionsTracker = TRACKER_HOSTS.some((host) => message.includes(host));
  if (!mentionsTracker) return false;

  return NOISE_PATTERNS.some((pattern) => pattern.test(message));
};

export const installTrackerNoiseSuppression = () => {
  if (typeof window === "undefined") return;
  if ((window as any).__trackerNoiseSuppressed) return;
  (window as any).__trackerNoiseSuppressed = true;

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    if (isTrackerNoise(args)) return;
    originalError(...args);
  };

  console.warn = (...args: unknown[]) => {
    if (isTrackerNoise(args)) return;
    originalWarn(...args);
  };

  // Swallow window.onerror entries that originate from tracker scripts
  window.addEventListener(
    "error",
    (event) => {
      const src = (event.target as HTMLScriptElement | HTMLImageElement | null)
        ?.getAttribute?.("src");
      if (src && TRACKER_HOSTS.some((host) => src.includes(host))) {
        event.stopImmediatePropagation();
      }
    },
    true
  );
};
